import { ArrowLineLeftIcon, WarningIcon, XIcon } from '@phosphor-icons/react'
import { ActionIcon, Box, Group, Stack, Text, Tooltip } from '@mantine/core'
import { memo, useLayoutEffect, useRef, useState } from 'react'
import classes from './SelectionPanel.module.css'
import {
  canSubmitSelection,
  getSubmitSelectionValidationMessage,
  isBoggleSelectionPreviouslyScored,
} from '~/boggle/engine/standard/game'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import { PlayButton } from '../atoms/Buttons'

function getSelectedWord(state: BoggleGameState) {
  return state.selection
    .map(position => state.board[position.row]?.[position.col]?.letter ?? '')
    .join('')
}

export const SelectionWordStrip = memo(function SelectionWordStrip({
  isPaused = false,
  state,
  store,
}: {
  isPaused?: boolean
  state: BoggleGameState
  store: BoggleStore
}) {
  const selectedWord = getSelectedWord(state)
  const showSelectionUi =
    selectedWord.length > 0 && state.phase === 'playing' && !isPaused
  const wordScrollRef = useRef<HTMLDivElement>(null)
  const [wrapperPulseKey, setWrapperPulseKey] = useState(0)
  const [wrapperPulseVariant, setWrapperPulseVariant] = useState<'gray' | 'red'>('gray')

  useLayoutEffect(() => {
    const el = wordScrollRef.current
    if (!el) return
    el.scrollLeft = el.scrollWidth - el.clientWidth
  }, [selectedWord])

  return (
    <Group justify="center" w="100%">
      <Group
        align="center"
        bdrs={0}
        gap={0}
        h={60}
        justify="space-between"
        pos="relative"
        w="100%"
        bg="dark.8"
        wrap="nowrap"
        style={{
          visibility: showSelectionUi ? 'visible' : 'hidden',
          boxShadow: '0 0 0 1px inset var(--mantine-color-dark-5)',
          overflow: 'visible',
        }}
      >
        {wrapperPulseKey > 0 ? (
          <Box
            key={wrapperPulseKey}
            className={
              wrapperPulseVariant === 'gray'
                ? classes.pulseOutlineGray
                : classes.pulseOutlineRed
            }
            pos="absolute"
            inset={0}
            h="100%"
            style={{ pointerEvents: 'none' }}
            w="100%"
          />
        ) : null}
        <Group
          gap={0}
          pos="relative"
          flex={1}
          pr={46}
          h="100%"
          miw={0}
          onPointerDown={() => {
            store.removeLastSelectionEntry()
            setWrapperPulseVariant('gray')
            setWrapperPulseKey(k => k + 1)
          }}
        >
          <Box
            ref={wordScrollRef}
            className={classes.wordStripScroll}
            display="flex"
            style={{ alignItems: 'center' }}
          >
            <Text
              pl={22}
              pb={2}
              fz={32}
              lh={1}
              ta="left"
              lts="0.05em"
              tt="uppercase"
              ff="Noto Sans Variable"
              fw={500}
              style={{ whiteSpace: 'nowrap' }}
            >
              {selectedWord}
            </Text>
          </Box>
          <ActionIcon
            aria-label="Remove last selected letter"
            bdrs={0}
            c="dark.4"
            bg="transparent"
            h={60}
            w={60}
            flex="0 0 60px"
            variant="subtle"
            pos="absolute"
            right={0}
            bottom={0}
          >
            <IconGlyph icon={ArrowLineLeftIcon} size={24} />
          </ActionIcon>
        </Group>
        <ActionIcon
          aria-label="Clear selection"
          color="red"
          c="red.4"
          bdrs={0}
          h={60}
          w={60}
          variant="light"
          style={{
            borderLeft: '1px solid var(--mantine-color-dark-5)',
            borderTopRightRadius: 5,
            borderBottomRightRadius: 5,
            flexShrink: 0,
          }}
          onClick={() => {
            store.clearSelection()
            setWrapperPulseVariant('red')
            setWrapperPulseKey(k => k + 1)
          }}
        >
          <IconGlyph icon={XIcon} size={26} />
        </ActionIcon>
      </Group>
    </Group>
  )
})

export const SubmitPlayRow = memo(function SubmitPlayRow({
  isPaused = false,
  playAccentColor = 'green',
  state,
  store,
}: {
  isPaused?: boolean
  playAccentColor?: string
  state: BoggleGameState
  store: BoggleStore
}) {
  const canPlaySelection = canSubmitSelection(state)
  const playSelectionBlockReason = getSubmitSelectionValidationMessage(state)
  const isReplaySelection = isBoggleSelectionPreviouslyScored(state)
  const showSelectionActions =
    state.phase === 'playing' &&
    state.activeSkill === null &&
    state.selection.length > 0 &&
    !isPaused

  return (
    <Stack align="center" gap={4} h={70} justify="center" w="70%" pos="relative">
      {showSelectionActions ? (
        <>
          <Tooltip
            // bg="red.4"
            c="yellow.4"
            disabled={canPlaySelection}
            events={{ hover: true, focus: true, touch: true }}
            label={
              playSelectionBlockReason ? (
                <Group gap={7}>
                  <IconGlyph icon={WarningIcon} size={20} weight="light" />
                  {playSelectionBlockReason}
                </Group>
              ) : null
            }
            position="bottom"
            withArrow={false}
          >
            <PlayButton
              accentColor={isReplaySelection ? '#ffd781' : playAccentColor}
              disabled={!canPlaySelection}
              label={isReplaySelection ? 'Replay' : 'Play'}
              w="100%"
              onClick={() => {
                store.submitSelection()
              }}
              {...(isReplaySelection ? { bg: '#1e1d17', c: '#ffd781' } : {})}
              style={
                isReplaySelection
                  ? {
                      border: '2px solid #ffd781',
                      boxShadow: '0 0 8px 0 rgba(255, 215, 129, 0.35)',
                    }
                  : undefined
              }
            />
          </Tooltip>
        </>
      ) : null}
    </Stack>
  )
})

export const SelectionPanel = memo(function SelectionPanel({
  isPaused = false,
  playAccentColor = 'green',
  state,
  store,
}: {
  isPaused?: boolean
  playAccentColor?: string
  state: BoggleGameState
  store: BoggleStore
}) {
  return (
    <Stack gap={10} px={20}>
      <SelectionWordStrip isPaused={isPaused} state={state} store={store} />
      <SubmitPlayRow
        isPaused={isPaused}
        playAccentColor={playAccentColor}
        state={state}
        store={store}
      />
    </Stack>
  )
})
